import React, { useState, useEffect } from 'react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import MobileStickyBar from '../components/common/MobileStickyBar';
import { toast } from 'sonner';
import { Star, MapPin, Phone, Mail, ExternalLink, ArrowRight, Heart, ChevronRight } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CAT_ICONS = {
  'caves-vins': '🍷',
  traiteurs: '🍴',
  dj: '🎧',
  photographes: '📷',
  fleuristes: '🌸',
  decorateurs: '🎨',
  lieux: '🏠',
};

export default function PartenairesPage() {
  const [partenaires, setPartenaires] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategorie, setActiveCategorie] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCandidature, setShowCandidature] = useState(false);
  const [candidature, setCandidature] = useState({ nom: '', categorie: '', email: '', telephone: '', zone: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let url = `${BACKEND}/api/partenaires`;
    if (activeCategorie) url += `?categorie=${activeCategorie}`;
    fetch(url)
      .then(r => r.json())
      .then(d => { setPartenaires(d.partenaires || []); setCategories(d.categories || []); })
      .catch((err) => { /* eslint-disable-line no-empty */ })
      .finally(() => setLoading(false));
  }, [activeCategorie]);

  async function submitCandidature() {
    if (!candidature.email || !candidature.nom) { toast.error('Email et nom requis'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/partenaires/candidature`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(candidature)
      });
      const data = await res.json();
      if (data.success) { toast.success(data.message); setShowCandidature(false); }
    } catch { toast.error('Erreur envoi'); }
    setSubmitting(false);
  }

  // Partenaire vedette (3 Caves)
  const featuredPartner = partenaires.find(p => p.featured);
  const others = partenaires.filter(p => !p.featured);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ minHeight: 260 }}>
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(93,188,191,0.14) 0%, rgba(212,175,55,0.10) 50%, transparent 100%)'
        }} />
        <div className="container-ose relative z-10" style={{ paddingTop: '3rem', paddingBottom: '2.5rem' }}>
          <div className="flex items-center gap-3 mb-3">
            <span style={{ fontSize: 36 }}>🤝</span>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--teal)' }}>Réseau local</p>
              <h1 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                Nos partenaires
              </h1>
            </div>
          </div>
          <p className="text-sm max-w-xl" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
            Artisans, prestataires et domaines sélectionnés dans le Lot pour compléter votre événement.
            Chaque partenaire est validé par nos équipes.
          </p>
        </div>
      </div>

      <div className="container-ose" style={{ paddingBottom: '5rem' }}>

        {/* Filtres catégories */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          <button onClick={() => setActiveCategorie('')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all"
            style={{
              background: !activeCategorie ? 'var(--teal)' : '#FFFFFF',
              color: !activeCategorie ? '#0B1E20' : 'var(--text-muted)',
              border: `1px solid ${!activeCategorie ? 'var(--teal)' : 'var(--border)'}`,
            }}>
            Tous
          </button>
          {categories.map(cat => (
            <button key={cat.id} onClick={() => setActiveCategorie(cat.id)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all"
              style={{
                background: activeCategorie === cat.id ? 'var(--teal)' : '#FFFFFF',
                color: activeCategorie === cat.id ? '#0B1E20' : 'var(--text-muted)',
                border: `1px solid ${activeCategorie === cat.id ? 'var(--teal)' : 'var(--border)'}`,
              }}>
              {CAT_ICONS[cat.id] || '🤝'} {cat.label}
              {cat.featured && <span style={{ fontSize: 9, background: 'rgba(212,175,55,0.3)', color: '#7A5E00', padding: '1px 5px', borderRadius: 4 }}>PREMIUM</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1,2,3].map(i => <div key={`col-${i}`} className="h-64 rounded-2xl animate-pulse" style={{ background: 'rgba(26,26,46,0.06)' }} />)}
          </div>
        ) : (
          <>
            {/* Partenaire vedette */}
            {featuredPartner && (
              <div className="mb-8 rounded-2xl overflow-hidden" style={{ border: '2px solid rgba(212,175,55,0.4)', boxShadow: '0 8px 32px rgba(212,175,55,0.12)' }}>
                <div className="flex items-center gap-2 px-6 py-3" style={{ background: 'rgba(212,175,55,0.08)', borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
                  <span style={{ fontSize: 16 }}>★</span>
                  <p className="text-xs font-bold" style={{ color: '#D4AF37' }}>Partenaire premium</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-0" style={{ background: '#FFFFFF' }}>
                  {/* Image */}
                  <div className="relative overflow-hidden" style={{ minHeight: 240 }}>
                    <img src={featuredPartner.image} alt={featuredPartner.nom}
                      className="w-full h-full object-cover" style={{ minHeight: 240 }} loading="lazy" />
                    {featuredPartner.logo && (
                      <div className="absolute bottom-4 left-4 p-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.95)' }}>
                        <img src={featuredPartner.logo} alt={featuredPartner.nom} style={{ height: 44, width: 'auto', objectFit: 'contain' }} loading="lazy" />
                      </div>
                    )}
                  </div>
                  {/* Infos */}
                  <div className="p-6 flex flex-col justify-between">
                    <div>
                      <p className="text-xs font-semibold mb-1" style={{ color: 'var(--teal)' }}>
                        {CAT_ICONS[featuredPartner.categorie]} {categories.find(c => c.id === featuredPartner.categorie)?.label}
                      </p>
                      <h2 className="font-display text-xl font-bold mb-3" style={{ color: 'var(--ink)' }}>{featuredPartner.nom}</h2>
                      <p className="text-sm mb-4" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>{featuredPartner.description}</p>
                      <div className="flex flex-wrap gap-3">
                        <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--teal)' }}>
                          <MapPin size={12} /> {featuredPartner.zone}
                        </span>
                        {featuredPartner.note && (
                          <span className="flex items-center gap-1 text-xs" style={{ color: '#D4AF37' }}>
                            <Star size={12} /> {featuredPartner.note} / 5
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      {featuredPartner.telephone && (
                        <a href={`tel:${featuredPartner.telephone.replace(/\s/g, '')}`}
                          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
                          style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
                          <Phone size={14} /> {featuredPartner.telephone}
                        </a>
                      )}
                      {featuredPartner.site && (
                        <a href={featuredPartner.site} target="_blank" rel="noreferrer"
                          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
                          style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                          <ExternalLink size={14} /> Voir le site
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Grille partenaires */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {others.map(p => (
                <div key={p.id} className="card-premium overflow-hidden">
                  {/* Image */}
                  <div className="relative overflow-hidden" style={{ height: 180 }}>
                    <img src={p.image} alt={p.nom}
                      className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(26,26,46,0.5) 0%, transparent 60%)' }} />
                    <div className="absolute bottom-3 left-3">
                      <span className="text-xs font-bold px-2 py-1 rounded-full"
                        style={{ background: 'rgba(255,255,255,0.15)', color: '#FFFFFF', backdropFilter: 'blur(8px)' }}>
                        {CAT_ICONS[p.categorie]} {categories.find(c => c.id === p.categorie)?.label}
                      </span>
                    </div>
                  </div>
                  {/* Infos */}
                  <div className="p-4">
                    <h3 className="font-semibold text-sm mb-1.5" style={{ color: 'var(--ink)' }}>{p.nom}</h3>
                    <p className="text-xs mb-3" style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>{p.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--teal)' }}>
                          <MapPin size={11} /> {p.zone.split(',')[0]}
                        </span>
                        {p.note && <span className="text-xs" style={{ color: '#D4AF37' }}>★ {p.note}</span>}
                      </div>
                      {p.telephone && (
                        <a href={`tel:${p.telephone.replace(/\s/g, '')}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold"
                          style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
                          <Phone size={11} /> Appeler
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Devenir partenaire */}
            <div className="mt-12 p-8 rounded-2xl text-center"
              style={{ background: 'linear-gradient(135deg, rgba(93,188,191,0.1), rgba(212,175,55,0.08))', border: '1px solid rgba(93,188,191,0.2)' }}>
              <Heart size={36} className="mx-auto mb-4" style={{ color: 'var(--teal)' }} />
              <h2 className="font-display text-xl font-bold mb-3" style={{ color: 'var(--ink)' }}>
                Rejoignez notre réseau
              </h2>
              <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
                Artisan, prestataire, lieu ou domaine du Lot ? Rejoignez notre réseau de partenaires
                et bénéficiez de visibilité lors des événements Show Événement.
              </p>
              <button onClick={() => setShowCandidature(true)}
                className="btn-teal px-7 py-3.5 rounded-xl text-sm font-bold inline-flex items-center gap-2">
                Candidater <ChevronRight size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Modal candidature */}
      {showCandidature && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          style={{ background: 'rgba(26,26,46,0.5)', backdropFilter: 'blur(8px)' }}>
          <div className="w-full max-w-lg rounded-3xl overflow-hidden" style={{ background: '#FEFAF5' }}>
            <div className="flex items-center justify-between p-6 pb-4">
              <div>
                <h2 className="font-display text-xl font-bold" style={{ color: 'var(--ink)' }}>Candidature partenaire</h2>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Réponse sous 5 jours ouvrés</p>
              </div>
              <button onClick={() => setShowCandidature(false)} className="p-2 rounded-xl" style={{ background: 'var(--bg)', color: 'var(--text-muted)' }}>✕</button>
            </div>
            <div className="px-6 pb-6 space-y-3">
              {[
                { key: 'nom', label: 'Nom entreprise / prénom', placeholder: 'Ex: 3 Caves depuis 1875' },
                { key: 'email', label: 'Email *', placeholder: 'contact@exemple.fr', type: 'email' },
                { key: 'telephone', label: 'Téléphone', placeholder: '05 65 XX XX XX', type: 'tel' },
                { key: 'zone', label: 'Zone d\'intervention', placeholder: 'Figeac, Lot (46)...' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--ink)' }}>{f.label}</label>
                  <input type={f.type || 'text'} placeholder={f.placeholder}
                    value={candidature[f.key]} onChange={e => setCandidature(p => ({ ...p, [f.key]: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl text-sm"
                    style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                </div>
              ))}
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--ink)' }}>Catégorie</label>
                <select value={candidature.categorie} onChange={e => setCandidature(p => ({ ...p, categorie: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl text-sm"
                  style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                  <option value="">Choisir…</option>
                  <option value="caves-vins">Cave & Vins</option>
                  <option value="traiteurs">Traiteur</option>
                  <option value="dj">DJ & Sono</option>
                  <option value="photographes">Photographe</option>
                  <option value="fleuristes">Fleuriste</option>
                  <option value="decorateurs">Décorateur</option>
                  <option value="lieux">Lieu & domaine</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--ink)' }}>Description de vos prestations</label>
                <textarea rows={3} placeholder="Décrivez vos services..."
                  value={candidature.description} onChange={e => setCandidature(p => ({ ...p, description: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl text-sm resize-none"
                  style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
              </div>
              <button onClick={submitCandidature} disabled={submitting || !candidature.email || !candidature.nom}
                className="w-full btn-teal py-3.5 rounded-xl text-sm font-bold disabled:opacity-40">
                {submitting ? 'Envoi…' : 'Envoyer ma candidature'}
              </button>
            </div>
          </div>
        </div>
      )}

      <MobileStickyBar />
      <Footer />
    </div>
  );
}
